// @flow

import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Maybe } from "ramda-fantasy";
import classNames from "classnames";

import { identity } from "ramda";

import { type MedidaSelecionada, MODO_APP_NORMAL } from "../../../model";

import { Translate, useI18n } from "@quantum/i18n";
import { LinkButton } from "@quantum/ui";
import {
  alertaErro,
  deveDesabilitarEdicaoDeMedida,
  editarMedidaSelecionada,
  excluirMedidaSelecionada,
  marcarMedidaSelecionada,
  desmarcarMedidaSelecionada,
  restaurarNomePadraoDeMedidaSelecionada,
  renomearMedidaSelecionada,
  isCheckedMedidaSelecionada,
  getModoApp,
} from "../../../state";

import { Tooltip, WithTooltip } from "../tooltip";

import IconeRestaurarPadrao from "../../../assets/icone_recarregar.png";
import IconeRenomearMedida from "../../../assets/icone_renomear.png";
import IconeEditarMedida from "../../../assets/engrenagem.png";
import IconeExcluirMedida from "../../../assets/excluir_medida.gif";

import { maybeEmpty } from "../../../util";

import { useScrollTo } from "../../comum";

import "./medida-selecionada.css";

type Props = {
  medidaSelecionada: MedidaSelecionada,
  indice: number,
  disabled?: boolean,
  renomeavel?: boolean,
};

const removeColchetes = (nome: string): string =>
  nome
    .replace(/\[[^\]]*\]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const nomeMedida = (medidaSelecionada: MedidaSelecionada): string =>
  Maybe(medidaSelecionada)
    .chain(({ label, descricaoSelecao }) =>
      maybeEmpty(label ? label : descricaoSelecao)
    )
    .getOrElse("");

export default function MedidaSelecionadaComponent({
  medidaSelecionada,
  indice,
  disabled = false,
  renomeavel = true,
}: Props) {
  const { translate } = useI18n();
  const [sendoRenomeado, setSendoRenomeado] = useState(false);
  const dispatch = useDispatch();
  const rolarTela = useScrollTo();

  const marcada = useSelector(state =>
    isCheckedMedidaSelecionada(state, indice)
  );

  const modoApp = useSelector(state => getModoApp(state));

  const edicaoMedidaDesabilitada = useSelector(state =>
    deveDesabilitarEdicaoDeMedida(state, medidaSelecionada)
  );

  const { register, handleSubmit, reset } = useForm({
    mode: "onBlur",
    defaultValues: {
      campoNome:
        modoApp === MODO_APP_NORMAL
          ? removeColchetes(nomeMedida(medidaSelecionada))
          : nomeMedida(medidaSelecionada),
    },
  });

  const nome = nomeMedida(medidaSelecionada);

  React.useEffect(() => {
    reset({
      campoNome: modoApp === MODO_APP_NORMAL ? removeColchetes(nome) : nome,
    });
  }, [reset, modoApp, nome]);

  const aoClicarRenomear = useCallback(e => setSendoRenomeado(true), []);

  const aoClicarSalvar = useCallback(
    (data: { campoNome: string }) => {
      if (
        modoApp === MODO_APP_NORMAL &&
        (data.campoNome.includes("[") || data.campoNome.includes("]"))
      ) {
        return dispatch(
          alertaErro(translate("configuracaoRelatorio.naoPermiteColchetes"))
        );
      }
      dispatch(renomearMedidaSelecionada(indice, data.campoNome.trim()));
      setSendoRenomeado(false);
    },
    [dispatch, indice, modoApp, translate]
  );

  const aoClicarRestaurarPadrao = useCallback(
    e => {
      e.preventDefault();
      dispatch(restaurarNomePadraoDeMedidaSelecionada(indice));
      setSendoRenomeado(false);
    },
    [dispatch, indice]
  );

  const aoClicarCancelar = useCallback(e => {
    e.preventDefault();
    setSendoRenomeado(false);
  }, []);

  const aoMudarSelecao = useCallback(
    event =>
      dispatch(
        event.target.checked
          ? marcarMedidaSelecionada(indice)
          : desmarcarMedidaSelecionada(indice)
      ),
    [dispatch, indice]
  );

  const aoEditar = useCallback(() => {
    dispatch(editarMedidaSelecionada(indice));
    rolarTela("edicaoDeMedida");
  }, [dispatch, indice, rolarTela]);

  const aoExcluir = useCallback(
    () => dispatch(excluirMedidaSelecionada(indice)),
    [dispatch, indice]
  );

  return (
    <div className="MedidaSelecionada">
      <div className="MedidaSelecionada__checkbox">
        <input
          type="checkbox"
          onChange={aoMudarSelecao}
          disabled={disabled}
          checked={marcada}
        />
      </div>

      <div className="MedidaSelecionada__nome">{nome}</div>

      {!disabled &&
        (sendoRenomeado ? (
          <form
            onSubmit={handleSubmit(aoClicarSalvar)}
            className="MedidaSelecionada__formulario-renomear"
          >
            <input
              autoFocus
              type="text"
              name="campoNome"
              ref={register({ required: true })}
              maxLength={100}
              className="MedidaSelecionada__campo-nome"
            />
            <hr className="MedidaSelecionada__separador" />
            <div className="MedidaSelecionada__salvar-e-cancelar">
              <LinkButton
                type="submit"
                className="MedidaSelecionada__botao-link"
              >
                <Translate>configuracaoRelatorio.botaoSalvar</Translate>
              </LinkButton>
              {medidaSelecionada.descricaoSelecao && (
                <Tooltip
                  overlayClassName="MedidaSelecionada__tooltip-restaurar"
                  placement="top"
                  overlay={
                    <span>
                      <Translate
                        replacements={[medidaSelecionada.descricaoSelecao]}
                      >
                        configuracaoRelatorio.restaurarPara
                      </Translate>
                    </span>
                  }
                >
                  <LinkButton
                    type="button"
                    onClick={aoClicarRestaurarPadrao}
                    className="MedidaSelecionada__botao-link MedidaSelecionada__botao-restaurar-padrao"
                  >
                    <img src={IconeRestaurarPadrao} alt="" />
                  </LinkButton>
                </Tooltip>
              )}
              <LinkButton
                type="button"
                onClick={aoClicarCancelar}
                className="MedidaSelecionada__botao-link MedidaSelecionada__cancelar"
              >
                <Translate>telas.cancelar</Translate>
              </LinkButton>
            </div>
            <hr className="MedidaSelecionada__separador" />
          </form>
        ) : (
          renomeavel && (
            <div
              className="MedidaSelecionada__opcao-com-icone"
              onClick={aoClicarRenomear}
            >
              <img src={IconeRenomearMedida} alt="" />
              <Translate>configuracaoRelatorio.renomear</Translate>
            </div>
          )
        ))}

      {!disabled && (
        <WithTooltip
          enabled={edicaoMedidaDesabilitada}
          trigger="hover"
          overlayClassName="MedidaSelecionada__tooltip-editar"
          placement="topRight"
          overlay={
            <span>
              <Translate>
                configuracaoRelatorio.medidaNaoPodeSerEditada
              </Translate>
            </span>
          }
        >
          <div
            className={classNames(
              "MedidaSelecionada__opcao-com-icone",
              edicaoMedidaDesabilitada &&
                "MedidaSelecionada__opcao-com-icone--desabilitada"
            )}
            onClick={edicaoMedidaDesabilitada ? identity : aoEditar}
          >
            <img src={IconeEditarMedida} alt="" />
            <Translate>configuracaoRelatorio.botaoEditarMedida</Translate>
          </div>
        </WithTooltip>
      )}

      {!disabled && (
        <WithTooltip
          enabled={edicaoMedidaDesabilitada}
          trigger="hover"
          overlayClassName="MedidaSelecionada__tooltip-remover"
          placement="topRight"
          overlay={
            <span>
              <Translate>
                configuracaoRelatorio.medidaNaoPodeSerExcluida
              </Translate>
            </span>
          }
        >
          <div
            className={classNames(
              "MedidaSelecionada__opcao-com-icone",
              edicaoMedidaDesabilitada &&
                "MedidaSelecionada__opcao-com-icone--desabilitada"
            )}
            onClick={edicaoMedidaDesabilitada ? identity : aoExcluir}
          >
            <img src={IconeExcluirMedida} alt="" />
          </div>
        </WithTooltip>
      )}
    </div>
  );
}
